import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const STAFF_PREFIXES = ["/admin", "/pos", "/kitchen", "/ops", "/clock", "/myhours"];

/** Owner-uploaded full-page background (image or video) behind the customer
 *  storefront. Sits at -z-20, below the seasonal decorations (-z-10); the
 *  cream shell goes translucent via html[data-bg] CSS so it shows through
 *  while cards/text stay readable. Staff screens are never affected. */
export default function SiteBackground() {
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  const videoRef = useRef(null);

  // Phones (esp. iOS) pause autoplaying background video on low-power mode,
  // tab switches, or brief network stalls. Nudge it back to playing.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const resume = () => {
      if (document.visibilityState !== "visible") return;
      v.play().catch(() => {});
    };
    const onStalled = () => {
      // Re-seek to the current time to kick the buffer loose.
      try { v.currentTime = v.currentTime + 0.001; } catch { /* ignore */ }
      resume();
    };
    v.addEventListener("pause", resume);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("suspend", resume);
    v.addEventListener("ended", resume);
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("touchstart", resume, { passive: true, once: true });
    return () => {
      v.removeEventListener("pause", resume);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("suspend", resume);
      v.removeEventListener("ended", resume);
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
      window.removeEventListener("touchstart", resume);
    };
  }, [settings.background_url, settings.background_type]);
  const isStaff = STAFF_PREFIXES.some((p) => pathname.startsWith(p));
  const type = settings.background_type || "none";
  const url = settings.background_url || "";
  const opacity = Math.max(0, Math.min(100, settings.background_opacity ?? 100)) / 100;
  const active = !isStaff && type !== "none" && !!url;

  useEffect(() => {
    if (active) document.documentElement.setAttribute("data-bg", "1");
    else document.documentElement.removeAttribute("data-bg");
    return () => document.documentElement.removeAttribute("data-bg");
  }, [active]);

  if (!active) return null;
  return (
    <div
      aria-hidden="true"
      data-testid="site-background"
      className="fixed inset-0 -z-20 pointer-events-none overflow-hidden select-none"
    >
      {type === "video" ? (
        <video
          ref={videoRef}
          key={url}
          src={url}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload noplaybackrate noremoteplayback"
          className="w-full h-full object-cover"
          style={{ pointerEvents: "none", userSelect: "none", opacity }}
        />
      ) : (
        <img src={url} alt="" className="w-full h-full object-cover" style={{ opacity }} />
      )}
      {/* light cream wash so text on the page stays readable */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(253, 251, 247, 0.42)" }}
      />
    </div>
  );
}
